// frontend/src/App.js
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./App.css";

import AthleteView from "./AthleteView";
import ProfileTab from "./ProfileTab";
import CoachView from "./CoachView";
import ParentView from "./ParentView";
import AthleteLineupsReadOnly from "./AthleteLineupsReadOnly";
import Login from "./Login";
import AcceptInvite from "./AcceptInvite";
import ResetPassword from "./ResetPassword";
import { PushNotifications } from "@capacitor/push-notifications"; // ✅ already imported
import { Capacitor } from "@capacitor/core";
import { waitForFCMToken } from "./fcmTokenBridge";
import { apiFetch } from "./api";


import {
  FaHome,
  FaCalendarAlt,
  FaUser,
  FaSitemap,
  FaUsers,
  FaEllipsisH,
  FaCog,
  FaWrench,
  FaTable,
  FaStopwatch,
  FaDumbbell,
  FaBars,
  FaShip,
} from "react-icons/fa";

const today = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
})();

/* ---------------- Helpers ---------------- */
function publicScreenFromPath() {
  const path = window.location.pathname;
  if (path === "/accept-invite") return "accept-invite";
  if (path === "/reset") return "reset";
  return null;
}

function parseOyoDeepLink() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "";
  const match = path.match(/^\/oyo\/(\d+)/i);
  if (match && match[1]) {
    const id = Number(match[1]);
    return Number.isFinite(id) ? id : null;
  }
  const params = new URLSearchParams(window.location.search || "");
  const queryId = params.get("oyo");
  if (queryId) {
    const id = Number(queryId);
    return Number.isFinite(id) ? id : null;
  }
  return null;
}

function parseLineupDeepLink() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "";
  const match = path.match(/^\/lineups\/(\d{4}-\d{2}-\d{2})/i);
  if (match && match[1]) return match[1];
  const params = new URLSearchParams(window.location.search || "");
  const queryDate = params.get("lineups");
  if (queryDate && /^\d{4}-\d{2}-\d{2}$/.test(queryDate)) return queryDate;
  return null;
}

function getToken() {
  try {
    const token =
      localStorage.getItem("authToken") || localStorage.getItem("coachToken");
    if (token && !localStorage.getItem("authToken")) {
      localStorage.setItem("authToken", token);
      localStorage.removeItem("coachToken");
    }
    return token || null;
  } catch {
    return null;
  }
}

function clearToken() {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("coachToken");
  } catch {}
}

function getStoredPushToken() {
  try {
    const raw = localStorage.getItem("pendingPushToken");
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.value) {
        if (parsed.platform === "ios" && parsed.type !== "fcm") {
          // Legacy APNs token stored before FCM bridge existed
          return null;
        }
        return parsed.value;
      }
    } catch {
      // Legacy plain string tokens were only valid for non-iOS platforms
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
        return raw;
      }
    }
  } catch {
    // ignore storage errors
  }
  return null;
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/* ============================================================
   App
============================================================ */
export default function App() {
  const [sessionUser, setSessionUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [pendingLineupDate, setPendingLineupDate] = useState(() => parseLineupDeepLink());
  const [athleteTab, setAthleteTab] = useState(() => (pendingLineupDate ? "lineups" : "home"));
  const [athleteLineupDate, setAthleteLineupDate] = useState(() => pendingLineupDate || today);
  const [parentTab, setParentTab] = useState("home");
  const [coachTab, setCoachTab] = useState("home");
  const [coachNavMoreOpen, setCoachNavMoreOpen] = useState(false);
  const [coachOverflowEnabled, setCoachOverflowEnabled] = useState(false);
  const [nativePushToken, setNativePushToken] = useState(() => getStoredPushToken());
  const [coachNavSignal, setCoachNavSignal] = useState(null);
  const [pendingOyoWorkoutId, setPendingOyoWorkoutId] = useState(() => parseOyoDeepLink());
  const footerNavRef = useRef(null);
  const sessionRole = (sessionUser?.role || "").toLowerCase();
  const sessionSecondaryRole = (sessionUser?.secondary_role || "").toLowerCase() || null;
  const isDualRole = !!sessionSecondaryRole;
  const [activeView, setActiveView] = useState(null);
  const effectiveRole = activeView || sessionRole;

  function switchToOtherRole() {
    const otherRole = effectiveRole === sessionRole ? sessionSecondaryRole : sessionRole;
    setActiveView(otherRole);
    if (otherRole === "athlete") setAthleteTab("home");
    else if (otherRole === "coach") setCoachTab("home");
    else if (otherRole === "parent") setParentTab("home");
  }

  const switchLabel = isDualRole
    ? `Switch to ${capitalize(effectiveRole === sessionRole ? sessionSecondaryRole : sessionRole)} View`
    : null;

  const switchMenuItem = switchLabel ? [{ label: switchLabel, onClick: switchToOtherRole }] : [];
  const canViewWorkouts =
    sessionUser?.permissions?.includes?.("workouts:view") ??
    sessionUser?.can_view_workouts ??
    true;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handler = (event) => {
      const detail = event?.detail || {};
      if (detail.tab) {
        setCoachTab(detail.tab);
      }
      setCoachNavSignal({
        tab: detail.tab || null,
        payload: detail.payload || null,
        ts: Date.now(),
      });
    };
    window.addEventListener("coach:navigate", handler);
    return () => window.removeEventListener("coach:navigate", handler);
  }, []);

  // Clear deep link path/query once we've processed auth to avoid retriggering
  useEffect(() => {
    if (authChecked && pendingOyoWorkoutId) {
      window.history.replaceState({}, "", "/");
    }
  }, [authChecked, pendingOyoWorkoutId]);

  useEffect(() => {
    if (authChecked && pendingLineupDate) {
      window.history.replaceState({}, "", "/");
      setPendingLineupDate(null);
    }
  }, [authChecked, pendingLineupDate]);

  // 🔔 Push Notifications setup
  useEffect(() => {
    let isMounted = true;
    const handles = [];

    async function initPush() {
      // ✅ Only run on iOS or Android native builds
      if (!Capacitor.isNativePlatform()) {
        console.log("Skipping push setup — web environment detected.");
        return;
      }

      try {
        // 1️⃣ Request permission
        const permStatus = await PushNotifications.requestPermissions();
        if (permStatus.receive !== "granted") {
          console.warn("Push permission not granted");
          return;
        }

        // 2️⃣ Register device for push notifications
        await PushNotifications.register();

        const persistToken = (tokenValue, metadata = {}) => {
          if (!isMounted || !tokenValue) return;
          setNativePushToken(tokenValue);
          try {
            localStorage.setItem(
              "pendingPushToken",
              JSON.stringify({
                value: tokenValue,
                platform: Capacitor.getPlatform(),
                type: metadata.type || "fcm",
                storedAt: Date.now(),
              })
            );
          } catch {
            // storage optional
          }
        };

        // 3️⃣ Handle successful registration (works for both iOS and Android)
        handles.push(
          PushNotifications.addListener("registration", async (token) => {
            if (!isMounted) return;

            if (Capacitor.getPlatform() === "ios") {
              console.log("APNs registration token received, waiting for FCM token...");
              const fcmToken = await waitForFCMToken();
              if (fcmToken) {
                persistToken(fcmToken, { type: "fcm" });
              } else {
                console.warn("FCM token not yet available from Firebase.");
              }
            } else {
              console.log("Push registration token:", token.value);
              persistToken(token.value, { type: "fcm" });
            }
          })
        );

        // 4️⃣ Handle registration errors
        handles.push(
          PushNotifications.addListener("registrationError", (err) => {
            console.error("Push registration error:", err.error || err);
          })
        );

        // 5️⃣ Handle notifications received while app is running
        handles.push(
          PushNotifications.addListener("pushNotificationReceived", (notification) => {
            console.log("Push received:", notification);
          })
        );

        // 6️⃣ Handle taps on notifications
        handles.push(
          PushNotifications.addListener("pushNotificationActionPerformed", (notification) => {
            console.log("Notification action performed:", notification);
          })
        );

        // Ensure we wait for FCM token even if registration event fired earlier
        if (Capacitor.getPlatform() === "ios") {
          const fcmToken = await waitForFCMToken();
          if (fcmToken) {
            persistToken(fcmToken, { type: "fcm" });
          }
        }

      } catch (err) {
        console.error("Push init failed:", err);
      }
    }

    initPush();

    return () => {
      isMounted = false;
      handles.forEach(async (handlePromise) => {
        try {
          const handle = await handlePromise;
          await handle.remove();
        } catch {
          // ignore listener cleanup errors
        }
      });
    };
  }, []);
  // 🔔 End push setup

  useEffect(() => {
    if (!nativePushToken || !sessionUser) return;
    if (!Capacitor.isNativePlatform()) return;

    let canceled = false;
    (async () => {
      try {
        await apiFetch("/push/register", {
          method: "POST",
          body: JSON.stringify({
            token: nativePushToken,
            platform: Capacitor.getPlatform(),
            team: sessionUser?.team || null,
          }),
        });
        if (canceled) return;
        if (!canceled) {
          try {
            localStorage.removeItem("pendingPushToken");
          } catch {
            // ignore storage failures
          }
        }
      } catch (err) {
        console.error("push token register error:", err);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [nativePushToken, sessionUser]);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const element = footerNavRef.current;
    if (!element) return () => {};

    const computeOverflowNeeded = () => {
      const widthBased = window.innerWidth <= 640;
      const needsOverflow = element.scrollWidth - element.clientWidth > 8;
      const enable = widthBased || needsOverflow;
      setCoachOverflowEnabled(enable);
      if (!enable) {
        setCoachNavMoreOpen(false);
      }
    };

    computeOverflowNeeded();

    let resizeObserver;
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(() => computeOverflowNeeded());
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", computeOverflowNeeded);
    window.addEventListener("orientationchange", computeOverflowNeeded);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", computeOverflowNeeded);
      window.removeEventListener("orientationchange", computeOverflowNeeded);
    };
  }, [canViewWorkouts, sessionUser]);

  // hydrate session from token
  useEffect(() => {
    (async () => {
      const token = getToken();
      if (!token) {
        setAuthChecked(true);
        return;
      }
      try {
        const me = await apiFetch("/auth/me");
        setSessionUser(me);
      } catch {
        clearToken();
        setSessionUser(null);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  useEffect(() => {
    setCoachNavMoreOpen(false);
  }, [coachTab, sessionUser?.role]);

  /* ---------- Public single-use screens ---------- */
  const screen = publicScreenFromPath();


  if (!sessionUser) {
    if (screen === "accept-invite") {
      return (
        <Frame title="TBC Racing">
          <div className="auth-container">
            <div className="card">
              <AcceptInvite
                onSuccess={(u) => {
                  setSessionUser(u);
                  window.history.replaceState({}, "", "/");
                }}
              />
            </div>
          </div>
        </Frame>
      );
    }
    if (screen === "reset") {
      return (
        <Frame title="TBC Racing">
          <div className="auth-container">
            <div className="card">
              <ResetPassword
                onSuccess={(u) => {
                  setSessionUser(u);
                  window.history.replaceState({}, "", "/");
                }}
              />
            </div>
          </div>
        </Frame>
      );
    }
  }

  /* ---------- Loading while checking token ---------- */
  if (!authChecked) {
    return (
      <Frame title="Loading">
        <div className="card">Checking session…</div>
      </Frame>
    );
  }

  /* ---------- Not logged in ---------- */
  if (!sessionUser) {
    return (
      <Frame title="TBC Racing">
        <div className="auth-container">
          <div className="card">
            <Login
              onSuccess={(me) => {
                setSessionUser(me);
                setActiveView(null);
                const nextRole = (me?.role || "").toLowerCase();
                if (nextRole === "athlete") setAthleteTab("home");
                if (nextRole === "coach") setCoachTab("home");
              }}
            />
          </div>
        </div>
      </Frame>
    );
  }


  /* ---------- Logged in as COACH (restored nav) ---------- */
  if (effectiveRole === "coach") {
    const coachMobileNav = [
      { tab: "home", label: "Home", icon: <FaHome /> },
      { tab: "lineups", label: "Lineups", icon: <FaSitemap /> },
      { tab: "launchgrid", label: "Launches", icon: <FaShip /> },
      { tab: "maintenance", label: "Tasks", icon: <FaWrench /> },
      ...(canViewWorkouts ? [{ tab: "workouts", label: "Workouts", icon: <FaStopwatch /> }] : []),
      { tab: "roster", label: "Roster", icon: <FaUsers /> },
      { tab: "attendance", label: "Attendance", icon: <FaTable /> },
      { tab: "calendar", label: "Calendar", icon: <FaCalendarAlt /> },
      ...((sessionUser?.can_access_config || sessionUser?.can_manage_equipment) ? [{ tab: "config", label: "Config", icon: <FaCog /> }] : []),
      { tab: "profile", label: "Profile", icon: <FaUser /> },
    ];
    return (
      <div className="screen">
        <Header
          title="Coach"
          user={sessionUser}
          mobileNavItems={coachMobileNav}
          activeTab={coachTab}
          onNavClick={setCoachTab}
          extraMenuItems={switchMenuItem}
          rightButton={{
            label: "Logout",
            onClick: () => {
              clearToken();
              setSessionUser(null);
              setCoachTab("home");
            },
          }}
        />
        
        <div className="container">
          {/* CoachView should read activeTab and render the right subpage */}
          <CoachView
            activeTab={coachTab}
            coach={sessionUser}
            navSignal={coachNavSignal}
            onNavigateToCalendar={() => setCoachTab("calendar")}
            initialOyoWorkoutId={pendingOyoWorkoutId}
            onConsumeOyoWorkoutLink={() => setPendingOyoWorkoutId(null)}
          />
        </div>

        {/* Bottom nav for COACH (buttons wired to setCoachTab) */}
        <div className="footer">
          <div
            className={`footer-inner ${coachOverflowEnabled ? "overflow-enabled" : ""}`}
            ref={footerNavRef}
          >
            <button
              className={`btn ghost footer-tab ${coachTab === "home" ? "active" : ""}`}
              onClick={() => setCoachTab("home")}
            >
              <FaHome /> Home
            </button>

            <button
              className={`btn ghost footer-tab ${coachTab === "lineups" ? "active" : ""}`}
              onClick={() => setCoachTab("lineups")}
            >
              <FaSitemap /> Lineups
            </button>
            <button
              className={`btn ghost footer-tab ${coachTab === "launchgrid" ? "active" : ""}`}
              onClick={() => setCoachTab("launchgrid")}
            >
              <FaShip /> Launches
            </button>

            <button
              className={`btn ghost footer-tab ${coachTab === "maintenance" ? "active" : ""}`}
              onClick={() => setCoachTab("maintenance")}
            >
              <FaWrench /> Tasks
            </button>

            {canViewWorkouts && (
              <button
                className={`btn ghost nav-optional footer-tab ${coachTab === "workouts" ? "active" : ""}`}
                onClick={() => setCoachTab("workouts")}
              >
                <FaStopwatch /> Workouts
              </button>
            )}
            <button
              className={`btn ghost nav-optional footer-tab ${coachTab === "roster" ? "active" : ""}`}
              onClick={() => setCoachTab("roster")}
            >
              <FaUsers /> Roster
            </button>
            <button
              className={`btn ghost nav-optional footer-tab ${coachTab === "attendance" ? "active" : ""}`}
              onClick={() => setCoachTab("attendance")}
            >
              <FaTable /> Attendance
            </button>
            <button
              className={`btn ghost nav-optional footer-tab ${coachTab === "calendar" ? "active" : ""}`}
              onClick={() => setCoachTab("calendar")}
            >
              <FaCalendarAlt /> Calendar
            </button>

            {(sessionUser?.can_access_config || sessionUser?.can_manage_equipment) && (
              <button
                className={`btn ghost nav-optional footer-tab ${coachTab === "config" ? "active" : ""}`}
                onClick={() => setCoachTab("config")}
              >
                <FaCog /> Config
              </button>
            )}
            <button
              className={`btn ghost nav-optional footer-tab ${coachTab === "profile" ? "active" : ""}`}
              onClick={() => setCoachTab("profile")}
            >
              <FaUser /> Profile
            </button>
            {coachOverflowEnabled && (
              <button
                className={`btn ghost nav-overflow-trigger footer-tab ${coachNavMoreOpen ? "active" : ""}`}
                onClick={() => setCoachNavMoreOpen((open) => !open)}
                type="button"
              >
                <FaEllipsisH /> More
              </button>
            )}
          </div>
        </div>
        {coachOverflowEnabled && coachNavMoreOpen && (
          <div className="nav-overflow-menu">
          
            {canViewWorkouts && (
              <button
                className={`nav-overflow-item ${coachTab === "workouts" ? "active" : ""}`}
                onClick={() => { setCoachTab("workouts"); setCoachNavMoreOpen(false); }}
              >
                <FaStopwatch /> Workouts
              </button>
            )}
            <button
              className={`nav-overflow-item ${coachTab === "roster" ? "active" : ""}`}
              onClick={() => { setCoachTab("roster"); setCoachNavMoreOpen(false); }}
            >
              <FaUsers /> Roster
            </button>
            <button
              className={`nav-overflow-item ${coachTab === "attendance" ? "active" : ""}`}
              onClick={() => { setCoachTab("attendance"); setCoachNavMoreOpen(false); }}
            >
              <FaTable /> Attendance
            </button>
            <button
              className={`nav-overflow-item ${coachTab === "calendar" ? "active" : ""}`}
              onClick={() => { setCoachTab("calendar"); setCoachNavMoreOpen(false); }}
            >
              <FaCalendarAlt /> Calendar
            </button>
            {(sessionUser?.can_access_config || sessionUser?.can_manage_equipment) && (
              <button
                className={`nav-overflow-item ${coachTab === "config" ? "active" : ""}`}
                onClick={() => { setCoachTab("config"); setCoachNavMoreOpen(false); }}
              >
                <FaCog /> Config
              </button>
            )}
            <button
              className={`nav-overflow-item ${coachTab === "profile" ? "active" : ""}`}
              onClick={() => { setCoachTab("profile"); setCoachNavMoreOpen(false); }}
            >
              <FaUser /> Profile
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ---------- Logged in as PARENT ---------- */
  if (effectiveRole === "parent") {
    const parentMobileNav = [
      { tab: "home", label: "Home", icon: <FaHome /> },
      { tab: "calendar", label: "Calendar", icon: <FaCalendarAlt /> },
    ];
    return (
      <div className="screen">
        <Header
          title="Parent"
          user={sessionUser}
          mobileNavItems={parentMobileNav}
          activeTab={parentTab}
          onNavClick={setParentTab}
          extraMenuItems={switchMenuItem}
          rightButton={{
            label: "Logout",
            onClick: () => {
              clearToken();
              setSessionUser(null);
              setParentTab("home");
            },
          }}
        />

        <div className="container">
          {parentTab === "home" && (
            <ParentView
              user={sessionUser}
              activeTab="home"
              onNavigateToCalendar={() => setParentTab("calendar")}
            />
          )}
          {parentTab === "calendar" && (
            <ParentView
              user={sessionUser}
              activeTab="calendar"
              onNavigateToCalendar={() => setParentTab("calendar")}
            />
          )}
        </div>

        <div className="footer">
          <div className="footer-inner">
            <button
              className={`btn ghost footer-tab ${parentTab === "home" ? "active" : ""}`}
              onClick={() => setParentTab("home")}
            >
              <FaHome /> Home
            </button>
            <button
              className={`btn ghost footer-tab ${parentTab === "calendar" ? "active" : ""}`}
              onClick={() => setParentTab("calendar")}
            >
              <FaCalendarAlt /> Calendar
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Logged in as ATHLETE ---------- */
  const athleteMobileNav = [
    { tab: "home", label: "Home", icon: <FaHome /> },
    { tab: "lineups", label: "Lineups", icon: <FaSitemap /> },
    ...(canViewWorkouts ? [{ tab: "workouts", label: "Workouts", icon: <FaDumbbell /> }] : []),
    { tab: "calendar", label: "Calendar", icon: <FaCalendarAlt /> },
    { tab: "profile", label: "Profile", icon: <FaUser /> },
  ];
  return (
    <div className="screen">
      <Header
        title="Athlete"
        user={sessionUser}
        mobileNavItems={athleteMobileNav}
        activeTab={athleteTab}
        onNavClick={setAthleteTab}
        extraMenuItems={switchMenuItem}
        rightButton={{
          label: "Logout",
          onClick: () => {
            clearToken();
            setSessionUser(null);
            setAthleteTab("home");
          },
        }}
      />

      <div className="container">
        {athleteTab === "home" && (
          <AthleteView
            user={sessionUser}
            activeTab="home"
            onNavigateToLineup={(date) => {
              setAthleteLineupDate(date);
              setAthleteTab("lineups");
            }}
            onNavigateToCalendar={() => setAthleteTab("calendar")}
            onNavigateToWorkouts={() => setAthleteTab("workouts")}
            initialOyoWorkoutId={pendingOyoWorkoutId}
            onConsumeOyoWorkoutLink={() => setPendingOyoWorkoutId(null)}
          />
        )}
        {athleteTab === "calendar" && (
          <AthleteView
            user={sessionUser}
            activeTab="calendar"
            onNavigateToCalendar={() => setAthleteTab("calendar")}
            initialOyoWorkoutId={pendingOyoWorkoutId}
            onConsumeOyoWorkoutLink={() => setPendingOyoWorkoutId(null)}
          />
        )}
        {athleteTab === "workouts" && (
          <AthleteView
            user={sessionUser}
            activeTab="workouts"
            initialOyoWorkoutId={pendingOyoWorkoutId}
            onConsumeOyoWorkoutLink={() => setPendingOyoWorkoutId(null)}
          />
        )}
       {athleteTab === "lineups" && sessionUser && (
         <div className="card">
           <AthleteLineupsReadOnly user={sessionUser} date={athleteLineupDate} />
         </div>
       )}
        {athleteTab === "profile" && <ProfileTab user={sessionUser} />}
      </div>

      {/* Bottom nav for ATHLETE */}
      <div className="footer">
        <div className="footer-inner">
          <button
            className={`btn ghost footer-tab ${athleteTab === "home" ? "active" : ""}`}
            onClick={() => setAthleteTab("home")}
          >
            <FaHome /> Home
          </button>
          <button
            className={`btn ghost footer-tab ${athleteTab === "lineups" ? "active" : ""}`}
            onClick={() => setAthleteTab("lineups")}
          >
            <FaSitemap /> Lineups
          </button>
          {canViewWorkouts && (
            <button
              className={`btn ghost footer-tab ${athleteTab === "workouts" ? "active" : ""}`}
              onClick={() => setAthleteTab("workouts")}
            >
              <FaDumbbell /> Workouts
            </button>
          )}
          <button
            className={`btn ghost footer-tab ${athleteTab === "calendar" ? "active" : ""}`}
            onClick={() => setAthleteTab("calendar")}
          >
            <FaCalendarAlt /> Calendar
          </button>
          <button
            className={`btn ghost footer-tab ${athleteTab === "profile" ? "active" : ""}`}
            onClick={() => setAthleteTab("profile")}
          >
            <FaUser /> Profile
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Small layout helpers ---------------- */
function Header({ title, rightButton, extraMenuItems = [], user, mobileNavItems = [], activeTab, onNavClick }) {
  const hasUser = Boolean(user);
  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  function openMenu() {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setMenuOpen((open) => !open);
  }

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (!e.target.closest(".profile-menu") && !e.target.closest(".profile-menu-panel")) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [menuOpen]);

  return (
    <div className="header">
      <div className="header-inner">
        <div className="header-left">
          <img src="/logo.png" alt="TBCRacing logo" className="brand-logo" />
        </div>
        <div className="header-center">
          <div className="header-title">TBC Racing</div>
        </div>
        <div className="header-right">
          {hasUser && rightButton && (
            <div className="profile-menu">
              <button
                ref={btnRef}
                className="profile-icon-btn hamburger-btn"
                onClick={openMenu}
                aria-label="Navigation menu"
              >
                <FaBars className="hamburger-icon" />
                <FaUser className="profile-icon" />
              </button>
              {menuOpen && createPortal(
                <div
                  className="profile-menu-panel mobile-nav-panel"
                  style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
                >
                  {mobileNavItems.map((item) => (
                    <button
                      key={item.tab}
                      className={`profile-menu-item mobile-nav-item ${activeTab === item.tab ? "active" : ""}`}
                      onClick={() => {
                        setMenuOpen(false);
                        onNavClick(item.tab);
                      }}
                    >
                      {item.icon} {item.label}
                    </button>
                  ))}
                  {mobileNavItems.length > 0 && <div className="profile-menu-divider" />}
                  {extraMenuItems.map((item, i) => (
                    <button
                      key={i}
                      className="profile-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        item.onClick();
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                  {extraMenuItems.length > 0 && <div className="profile-menu-divider" />}
                  <button
                    className="profile-menu-item"
                    onClick={() => {
                      setMenuOpen(false);
                      rightButton.onClick();
                    }}
                  >
                    {rightButton.label}
                  </button>
                </div>,
                document.body
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Frame({ title, rightButton, user = null, children }) {
  return (
    <div className="screen">
      <Header title={title} rightButton={rightButton} user={user} />
      <div className="container">{children}</div>
      <div className="footer">
        <div className="footer-inner" />
      </div>
    </div>
  );
}
